import React, { useContext, useEffect, useRef, useState } from 'react';
import {
  Button,
  CaptionText,
  CloseIcon,
  CodeIcon,
  DocumentIcon,
  OthersIcon
} from '@paypalcorp/pp-react';
import loadable from '@loadable/component';
import { useSelector } from 'react-redux';
import styles from './TestPanel.module.less';
import IntegrationPageSectionContext from '../IntegrationPageSection/StateManagement/IntegrationPageSectionContext';
import TestPanelContext from '../IntegrationPageSection/StateManagement/TestPanelContext';
import { getApiHistory } from './api';
import { trackTabClick } from '../../lib/fpti';
import Loading from '../../components/Loading/Loading';
import { isUserLoggedInSelector } from '../../store/App/selectors';
import { PARTNER } from '../../lib/constants';
import { useParams } from 'react-router';
import useSessionChecker from '../../util/useSessionChecker';

const LogViewer = loadable(() => import('./LogViewer'), {
  fallback: <Loading inverse size="lg" />
});
const TestAccounts = loadable(() => import('./TestAccounts'), {
  fallback: <Loading inverse size="lg" />
});
const ApiHistory = loadable(() => import('./ApiHistory'), {
  fallback: <Loading inverse size="lg" />
});

const TestPanel = () => {
  const panelHeading = 'Test Panel:';
  const isUserLoggedIn = useSelector(isUserLoggedInSelector);
  const appIntegrationType = useSelector((state) => state?.app?.appIntegrationType);
  const { product } = useParams();
  let headers = [
    {
      key: 'logs',
      title: 'Logs',
      Icon: OthersIcon,
      enabled: true
    },
    {
      key: 'testAccounts',
      title: 'Test accounts',
      Icon: CodeIcon,
      enabled: true
    },
    {
      key: 'apiHistory',
      title: 'API history',
      Icon: DocumentIcon,
      enabled: true
    }
    // {
    //     key: 'transactions',
    //     title: 'Transactions',
    //     iconName: 'others'
    // },
  ].filter((item) => item.enabled);

  if (appIntegrationType === PARTNER && product == 'fastlane' && isUserLoggedIn) {
    headers = [
      {
        key: 'logs',
        title: 'Logs',
        Icon: OthersIcon,
        enabled: true
      }
    ];
  }

  const csrfToken = useSelector((state) => state?.app?.csrfToken);

  const {
    SdkConfigurator: {
      sdkSettings: { clientId }
    }
  } = useContext(IntegrationPageSectionContext);

  const {
    isOpen: isTestPanelOpen,
    toggleTestPanel,
    apiHistory: { fetchingHistory, hasMounted },
    updateApiHistory
  } = useContext(TestPanelContext);

  const PANEL_MIN_HEIGHT = 55;
  let height;
  const closeBtnRef = useRef(null);
  const dragPanelRef = useRef(null);
  const dragHandleRef = useRef(null);

  const [headerIndex, setHeaderIndex] = useState(0);
  const [panelHeight, setPanelHeight] = useState(0);

  useEffect(() => {
    const refreshApiHistory = async () => {
      const history = await getApiHistory(csrfToken, clientId);
      updateApiHistory({ apiHistoryItems: history.items, fetchingHistory: false });
    };
    if (hasMounted && fetchingHistory && isUserLoggedIn) {
      setTimeout(async () => {
        refreshApiHistory();
      }, 500);
    }
  }, [fetchingHistory]);

  useEffect(() => {
    setHeaderIndex(0);
  }, [isTestPanelOpen]);

  const isNumber = (value) => {
    return typeof value === 'number';
  };

  useSessionChecker();

  useEffect(() => {
    if (closeBtnRef && closeBtnRef.current) {
      closeBtnRef.current.focus();
    }
  }, [isTestPanelOpen]);

  useEffect(() => {
    /* istanbul ignore if */
    if (isTestPanelOpen) {
      const panelElement = dragPanelRef.current;

      if (isNumber(panelHeight) && panelHeight !== 0) {
        panelElement.style.height = `${panelHeight}px`;
        height = panelHeight;
      } else {
        const panelStyle = window.getComputedStyle(panelElement);
        height = parseInt(panelStyle.height, 10);
      }

      let yCord = 0;

      const onMouseMoveResize = (event) => {
        const dy = event.clientY - yCord;
        height -= dy;
        yCord = event.clientY;

        const PANEL_MAX_HEIGHT = 0.85 * window.innerHeight;

        if (height > PANEL_MAX_HEIGHT) {
          height = parseInt(PANEL_MAX_HEIGHT, 10);
        } else if (height < PANEL_MIN_HEIGHT) {
          height = PANEL_MIN_HEIGHT;
        }

        panelElement.style.height = `${height}px`;
      };

      const onMouseUpResize = () => {
        document.removeEventListener('mousemove', onMouseMoveResize);
      };

      const onMouseDownResize = (event) => {
        yCord = event.clientY;
        document.addEventListener('mousemove', onMouseMoveResize);
        document.addEventListener('mouseup', onMouseUpResize);
      };

      const panelDragElement = dragHandleRef.current;
      panelDragElement.addEventListener('mousedown', onMouseDownResize);

      return () => {
        panelDragElement.removeEventListener('mousedown', onMouseDownResize);
      };
    }
    return undefined;
  }, [isTestPanelOpen]);

  const handleToggleTestPanel = () => {
    toggleTestPanel(!isTestPanelOpen);

    if (isTestPanelOpen) {
      dragPanelRef.current.style.removeProperty('height');

      /* istanbul ignore else */
      if (isNumber(height)) {
        setPanelHeight(height);
      }
    }
  };
  const handleTabClick = (currentIndex) => {
    trackTabClick(headers[currentIndex].title);
    setHeaderIndex(currentIndex);
  };

  return (
    <>
      {isTestPanelOpen ? (
        <>
          <div
            data-testid="resizable-container"
            ref={dragPanelRef}
            className={styles.panel_container}
          >
            <div className={styles.panel_header}>
              <div ref={dragHandleRef} className={styles.drag_container}>
                <div data-testid="resize-button" role="button" className={styles.drag_panel} />
              </div>
              <div className={styles.tab_container}>
                <div className={styles.tabs}>
                  <div className={styles.tab_heading}>{panelHeading}</div>
                  {headers.map(({ title, Icon }, currentHeaderIndex) => {
                    return (
                      <Button
                        key={title}
                        onClick={() => handleTabClick(currentHeaderIndex)}
                        className={
                          headerIndex === currentHeaderIndex
                            ? `${styles.tab} ${styles.active_container}`
                            : styles.tab
                        }
                        tertiary
                        size="sm"
                        iconComponent={Icon}
                      >
                        <CaptionText
                          type="large"
                          className={
                            headerIndex === currentHeaderIndex ? styles.title_active : styles.title
                          }
                        >
                          {title}
                        </CaptionText>
                      </Button>
                    );
                  })}
                </div>
                <Button
                  id="close-btn"
                  className={styles.close_container}
                  tertiary
                  size="sm"
                  iconComponent={CloseIcon}
                  onClick={handleToggleTestPanel}
                  ref={closeBtnRef}
                >
                  <CaptionText className={styles.close_text}>Close</CaptionText>
                </Button>
              </div>
            </div>
            <div className={styles.panel_content}>
              {headers[headerIndex].key === 'logs' && <LogViewer title="API Requests" />}
              {headers[headerIndex].key === 'testAccounts' && (
                <TestAccounts title="Test Accounts" />
              )}
              {headers[headerIndex].key === 'apiHistory' && <ApiHistory title="Api History" />}
            </div>
          </div>
        </>
      ) : (
        <>
          <div className={styles.panel_btn_container}>
            <Button
              size="sm"
              className={styles.panel_btn}
              inverse
              onClick={handleToggleTestPanel}
              aria-haspopup="true"
              iconComponent={CodeIcon}
            >
              Test Panel
            </Button>
          </div>
        </>
      )}
    </>
  );
};

export default TestPanel;
